import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ColumnConfig {
  key: string;
  header: string;
  priority: 1 | 2 | 3 | 4 | 5;
  render?: (value: any, row: any) => React.ReactNode;
  className?: string;
}

interface ResponsiveTableProps {
  data: any[];
  columns: ColumnConfig[];
  className?: string;
  onRowClick?: (row: any) => void;
  expandableContent?: (row: any) => React.ReactNode;
}

// Mobile value display component for legacy compatibility
export function MobileValue({ 
  label, 
  children, 
  className 
}: { 
  label: string; 
  children: React.ReactNode; 
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1 sm:hidden", className)}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div>{children}</div>
    </div>
  );
}

export function ResponsiveTable({ 
  data, 
  columns, 
  className,
  onRowClick,
  expandableContent 
}: ResponsiveTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRowExpansion = (rowId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const getHiddenColumns = (priority: number) => {
    return columns.filter(col => col.priority > priority);
  };

  return (
    <div className={cn("responsive-table overflow-hidden rounded-xl border border-border/50 bg-card/80 backdrop-blur-sm shadow-lg", className)}>
      <table className="w-full">
        <thead>
          <tr>
            {expandableContent && (
              <th className="w-8 p-2" aria-label="Expand row">
                <span className="sr-only">Details</span>
              </th>
            )}
            {columns.map(column => (
              <th
                key={column.key}
                data-priority={column.priority}
                className={cn(
                  "text-left p-3 font-medium text-muted-foreground border-b",
                  column.className
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => {
            const rowId = row.id || index.toString();
            const isExpanded = expandedRows.has(rowId);
            
            return (
              <React.Fragment key={rowId}>
                <tr 
                  className={cn(
                    "border-b hover:bg-muted/50 transition-colors",
                    onRowClick && "cursor-pointer"
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  {expandableContent && (
                    <td className="p-2">
                      <button
                        className={cn(
                          "table-row-expander",
                          isExpanded && "expanded"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowExpansion(rowId);
                        }}
                        aria-expanded={isExpanded}
                        aria-label="Toggle row details"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </td>
                  )}
                  {columns.map(column => (
                    <td
                      key={column.key}
                      data-priority={column.priority}
                      className={cn("p-3", column.className)}
                    >
                      {column.render 
                        ? column.render(row[column.key], row)
                        : row[column.key]
                      }
                    </td>
                  ))}
                </tr>
                
                {/* Expanded row details */}
                {isExpanded && expandableContent && (
                  <tr>
                    <td 
                      colSpan={columns.length + 1} 
                      className="table-row-details p-0"
                    >
                      <div className="p-4">
                        {expandableContent(row)}
                      </div>
                    </td>
                  </tr>
                )}
                
                {/* Show hidden columns in mobile view when expanded */}
                {isExpanded && !expandableContent && (
                  <>
                    {/* Hidden columns for small screens */}
                    <tr className="table-row-details sm:hidden">
                      <td colSpan={columns.length + 1}>
                        <dl className="table-row-details dl">
                          {getHiddenColumns(2).map(column => (
                            <React.Fragment key={`${rowId}-${column.key}`}>
                              <dt>{column.header}</dt>
                              <dd>
                                {column.render 
                                  ? column.render(row[column.key], row)
                                  : row[column.key]
                                }
                              </dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      </td>
                    </tr>
                    
                    {/* Hidden columns for medium screens */}
                    <tr className="table-row-details hidden sm:table-row md:hidden">
                      <td colSpan={columns.length + 1}>
                        <dl className="table-row-details dl">
                          {getHiddenColumns(3).map(column => (
                            <React.Fragment key={`${rowId}-${column.key}`}>
                              <dt>{column.header}</dt>
                              <dd>
                                {column.render 
                                  ? column.render(row[column.key], row)
                                  : row[column.key]
                                }
                              </dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      </td>
                    </tr>
                    
                    {/* Hidden columns for large screens */}
                    <tr className="table-row-details hidden md:table-row lg:hidden">
                      <td colSpan={columns.length + 1}>
                        <dl className="table-row-details dl">
                          {getHiddenColumns(4).map(column => (
                            <React.Fragment key={`${rowId}-${column.key}`}>
                              <dt>{column.header}</dt>
                              <dd>
                                {column.render 
                                  ? column.render(row[column.key], row)
                                  : row[column.key]
                                }
                              </dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      </td>
                    </tr>
                  </>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      
      {data.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          No data available
        </div>
      )}
    </div>
  );
}